export const STUDIO_REPORTING_PRIORITY_MODEL_VERSION = "a32.1_v1" as const;

export type StudioReportingPriorityTone = "attention" | "monitor";

export type StudioReportingPriorityItem = {
  key:
    | "callback_backlog"
    | "booking_exceptions"
    | "immediate_alerts"
    | "payment_exceptions"
    | "lifecycle_overdue"
    | "owner_pending";
  label: string;
  count: number;
  tone: StudioReportingPriorityTone;
  nextAction: string;
};

export type StudioReportingPriorityModel = {
  version: typeof STUDIO_REPORTING_PRIORITY_MODEL_VERSION;
  headline: string;
  attentionNow: StudioReportingPriorityItem[];
  monitor: StudioReportingPriorityItem[];
  prioritySummary: string;
  monitorSummary: string;
  scanSummary: string;
  firstPassSummary: string;
  firstAction: string;
  ownerSummary: string;
};

type StudioReportingPriorityInput = {
  callbackBacklogCount: number;
  bookingExceptionCount: number;
  immediateAlertCount: number;
  paymentExceptionCount: number;
  lifecycleOverdueCount: number;
  ownerPendingCount: number;
};

function safeCount(value: number) {
  return Math.max(0, Number(value || 0));
}

function formatItemList(items: StudioReportingPriorityItem[]) {
  if (!items.length) return "nothing immediate";
  return items.map((item) => `${item.label.toLowerCase()} ${item.count}`).join(" -> ");
}

export function buildStudioReportingPriorityModel(
  input: StudioReportingPriorityInput
): StudioReportingPriorityModel {
  const callbackBacklogCount = safeCount(input.callbackBacklogCount);
  const bookingExceptionCount = safeCount(input.bookingExceptionCount);
  const immediateAlertCount = safeCount(input.immediateAlertCount);
  const paymentExceptionCount = safeCount(input.paymentExceptionCount);
  const lifecycleOverdueCount = safeCount(input.lifecycleOverdueCount);
  const ownerPendingCount = safeCount(input.ownerPendingCount);

  const attentionNow: StudioReportingPriorityItem[] = [
    {
      key: "callback_backlog",
      label: "Callback backlog",
      count: callbackBacklogCount,
      tone: "attention",
      nextAction:
        callbackBacklogCount > 0
          ? "Clear callback ownership first so follow-up drift does not hide inside later queues."
          : "Callback backlog is clear enough for normal queue work.",
    },
    {
      key: "booking_exceptions",
      label: "Booking exceptions",
      count: bookingExceptionCount,
      tone: "attention",
      nextAction:
        bookingExceptionCount > 0
          ? "Review awaiting-confirmation and needs-staff cases after callback backlog is under control."
          : "Booking exceptions are not blocking the current queue.",
    },
    {
      key: "immediate_alerts",
      label: "Immediate alerts",
      count: immediateAlertCount,
      tone: "attention",
      nextAction:
        immediateAlertCount > 0
          ? "Act on unacknowledged or failed-delivery alerts before treating the system as stable."
          : "No immediate alert action is blocking the current pass.",
    },
  ].filter((item) => item.count > 0);

  const monitor: StudioReportingPriorityItem[] = [
    {
      key: "payment_exceptions",
      label: "Payment exceptions",
      count: paymentExceptionCount,
      tone: "monitor",
      nextAction:
        paymentExceptionCount > 0
          ? "Keep payment follow-up visible, but do not let it outrank callback, booking, or immediate alert work."
          : "Payment exceptions are quiet enough for normal monitoring.",
    },
    {
      key: "lifecycle_overdue",
      label: "Lifecycle overdue",
      count: lifecycleOverdueCount,
      tone: "monitor",
      nextAction:
        lifecycleOverdueCount > 0
          ? "Track overdue reminder, renewal, and abandoned-booking work as monitored pressure rather than first-pass queue work."
          : "Lifecycle overdue pressure is currently quiet.",
    },
    {
      key: "owner_pending",
      label: "Owner pending",
      count: ownerPendingCount,
      tone: "monitor",
      nextAction:
        ownerPendingCount > 0
          ? "Keep owner-pending items visible in the summary packet, but do not let them displace live queue work."
          : "No owner-pending summary items are currently blocking the packet.",
    },
  ].filter((item) => item.count > 0);

  const headline = attentionNow.length
    ? `${attentionNow[0].label} first`
    : monitor.length
    ? "No immediate action lanes"
    : "Scan is clear";

  const prioritySummary = attentionNow.length
    ? `Start with ${attentionNow[0].label.toLowerCase()} ${attentionNow[0].count}${attentionNow.length > 1 ? `, then ${attentionNow.slice(1).map((item) => `${item.label.toLowerCase()} ${item.count}`).join(", then ")}` : ""}.`
    : monitor.length
      ? `Immediate queue is clear. Monitor ${formatItemList(monitor)}.`
      : "Immediate queue is clear and monitored lanes are steady.";

  const monitorSummary = monitor.length
    ? formatItemList(monitor)
    : "no monitored lanes are currently pressing on the summary packet.";

  const scanSummary = attentionNow.length
    ? `Attention now: ${formatItemList(attentionNow)}`
    : "Attention now: no immediate action lanes are open.";

  const firstPassSummary = attentionNow.length
    ? `First pass: ${formatItemList(attentionNow)}`
    : monitor.length
      ? `First pass is clear. Monitor ${formatItemList(monitor)}.`
      : "First pass is clear.";

  const firstAction = attentionNow.length
    ? attentionNow[0].nextAction
    : monitor.length
      ? monitor[0].nextAction
      : "No immediate action is blocking the current operating pass.";

  const ownerSummary = attentionNow.length
    ? `Lead the owner summary with ${attentionNow[0].label.toLowerCase()} first, then note that ${monitor.length ? formatItemList(monitor) : "the monitored lanes remain steady"} stays under observation.`
    : monitor.length
    ? `No immediate action lanes are open. Keep ${formatItemList(monitor)} in the monitored-this-week line.`
    : "No immediate action lanes are open and the monitored lanes are steady enough for a concise owner update.";

  return {
    version: STUDIO_REPORTING_PRIORITY_MODEL_VERSION,
    headline,
    attentionNow,
    monitor,
    prioritySummary,
    monitorSummary,
    scanSummary,
    firstPassSummary,
    firstAction,
    ownerSummary,
  };
}
